import React from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form, Table, Alert } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import moment from 'moment'
import CreatableSelect from 'react-select/creatable'
import { ActionMeta, SingleValue } from 'react-select'
import { AssetTransaction, FinancialAsset, Fund, SellPreview } from 'types'
import Utils from 'common/Utils'

type TransactionMode = 'buy' | 'sell'

interface Props {
    onSuccess: (result: unknown) => void
    entryId?: number
    mode?: TransactionMode
}

function EditAssetTransactionWorkflow({ onSuccess, entryId, mode }: Props) {
    const isPreviewEligible = entryId === undefined && mode === 'sell'

    const loadingStep = (_params: unknown) => {
        const toFetch: Record<string, any> = {
            assets: API.get('/financial-assets'),
            funds: API.get('/funds')
        }
        if (entryId !== undefined) {
            toFetch.entry = API.get(`/asset-transactions/${entryId}`)
            // Check whether editing this transaction would invalidate the realized gains of later
            // sells of the same asset; warn the user before saving.
            toFetch.impact = API.get(`/asset-transactions/${entryId}/realized-gain-impact`).then((r: any) => r.data).catch(() => null)
        }
        return API.multiple(toFetch).then((r: any) => ({
            assets: (r.assets.results ?? []) as FinancialAsset[],
            funds: (r.funds.results ?? []) as Fund[],
            entry: r.entry as AssetTransaction | undefined,
            hasLaterSells: !!(r.impact?.hasLaterSells),
            laterSellsCount: (r.impact?.count ?? 0) as number
        }))
    }

    const userInputStep = (_params: unknown, loaded: unknown, previousValues: unknown, _errs: unknown, completion: (v: unknown) => void) => {
        const { assets, funds, entry, hasLaterSells, laterSellsCount } = loaded as {
            assets: FinancialAsset[]; funds: Fund[]; entry?: AssetTransaction
            hasLaterSells: boolean; laterSellsCount: number
        }
        const effectiveMode: TransactionMode = entry?.transactionType === 'SELL' ? 'sell' : (mode ?? 'buy')
        return {
            title: entryId ? `Edit ${effectiveMode}` : `New ${effectiveMode}`,
            component: <TransactionForm assets={assets} funds={funds} entry={entry} mode={effectiveMode} previousValues={previousValues} hasLaterSells={hasLaterSells} laterSellsCount={laterSellsCount} completion={completion} />
        }
    }

    const validationStep = isPreviewEligible
        ? (_p: unknown, _loaded: unknown, userInput: unknown) => {
            const payload = userInput as {
                createAsset?: { name: string; fundId: number }
                date: string
                financialAssetId?: number
                assetAmount: string
                euroAmount: string
                notes: string | null
            }
            if (payload.createAsset || !payload.financialAssetId) {
                return Promise.resolve({
                    success: false as const,
                    errors: { message: 'Please select an existing asset to sell from.' }
                })
            }
            const transactionPayload = {
                date: payload.date,
                financialAssetId: payload.financialAssetId,
                assetAmount: payload.assetAmount,
                euroAmount: payload.euroAmount,
                notes: payload.notes
            }
            return API.post('/asset-transactions/sell-preview', transactionPayload)
                .then((r: any) => ({ success: true as const, data: r.data as SellPreview }))
                .catch((err: any) => ({
                    success: false as const,
                    errors: { message: err?.response?.data?.message ?? err?.message ?? 'Failed to preview sale' }
                }))
        }
        : undefined

    const postValidationConfirmationStep = isPreviewEligible
        ? (_p: unknown, _loaded: unknown, _userInput: unknown, validationData: unknown) => {
            if (!validationData) return null
            return <SellPreviewConfirmation preview={validationData as SellPreview} />
        }
        : undefined

    const executionStep = (_params: unknown, _loaded: unknown, userInput: unknown) => {
        const payload = userInput as {
            createAsset?: { name: string; fundId: number }
            date: string
            financialAssetId?: number
            assetAmount: string
            euroAmount: string
            notes: string | null
        }

        const executeTransaction = (financialAssetId: number) => {
            const transactionPayload = {
                date: payload.date,
                financialAssetId,
                assetAmount: payload.assetAmount,
                euroAmount: payload.euroAmount,
                notes: payload.notes
            }
            if (entryId !== undefined) {
                return API.put(`/asset-transactions/${entryId}`, transactionPayload)
            }
            return API.post('/asset-transactions', transactionPayload)
        }

        if (payload.createAsset) {
            return API.post('/financial-assets', {
                name: payload.createAsset.name,
                fundId: payload.createAsset.fundId,
                notes: null
            }).then((response: any) => executeTransaction(response.data.id))
        }

        return executeTransaction(payload.financialAssetId!)
    }

    return (
        <OperationWorkflowModal
            loadingStep={loadingStep}
            userInputStep={userInputStep}
            validationStep={validationStep}
            postValidationConfirmationStep={postValidationConfirmationStep}
            canGoBackFromPostValidation={isPreviewEligible}
            backFromPostValidationLabel="Back to form"
            executionStep={executionStep}
            completionCallback={(r: unknown) => onSuccess(r && (r as any).data)}
        />
    )
}

interface FormProps {
    assets: FinancialAsset[]
    funds: Fund[]
    entry?: AssetTransaction
    mode: TransactionMode
    previousValues?: unknown
    hasLaterSells: boolean
    laterSellsCount: number
    completion: (v: unknown) => void
}

interface AssetOption {
    value: number | string
    label: string
    __isNew__?: boolean
}

function TransactionForm({ assets, funds, entry, mode, previousValues, hasLaterSells, laterSellsCount, completion }: FormProps) {
    const investmentFunds = funds.filter(f => f.isInvestment && !f.deleted)

    const prev = previousValues as {
        date?: string
        financialAssetId?: number
        assetAmount?: string
        euroAmount?: string
        notes?: string | null
        createAsset?: { name: string; fundId: number }
    } | undefined

    const schema = yup.object().shape({
        date: yup
            .string()
            .required('Date is required')
            .test('is-valid-date', 'Date must be a valid date in YYYY-MM-DD format', (value) => {
                if (!value) return false
                return moment(value, 'YYYY-MM-DD', true).isValid()
            }),
        financialAssetId: yup.number().nullable(),
        newAssetName: yup.string(),
        newAssetFundId: yup.number().nullable().when('newAssetName', {
            is: (value: string | undefined) => !!value?.trim(),
            then: (field) => field.required('Fund is required for a new asset'),
            otherwise: (field) => field.notRequired()
        }),
        assetAmount: yup.number().moreThan(0, 'Asset amount must be greater than zero').required('Asset amount is required'),
        euroAmount: mode === 'buy'
            ? yup.number().moreThan(0, 'Cost must be greater than zero').required('Cost is required')
            : yup.number().min(0, 'Proceeds cannot be negative').required('Proceeds is required'),
        notes: yup.string()
    }).test('asset-selected', function assetSelected(values) {
        if (!!values.financialAssetId || !!values.newAssetName?.trim()) {
            return true
        }
        return this.createError({ path: 'financialAssetId', message: 'Asset is required' })
    })

    const today = new Date().toISOString().split('T')[0]

    const initialValues = prev
        ? {
            date: prev.date ?? today,
            financialAssetId: prev.financialAssetId ?? null,
            newAssetName: prev.createAsset?.name ?? '',
            newAssetFundId: prev.createAsset?.fundId ?? investmentFunds[0]?.id ?? null,
            // stored amounts are signed (sell: -assetAmount, buy: -euroAmount); form uses absolute values
            assetAmount: prev.assetAmount != null ? String(Math.abs(Number(prev.assetAmount))) : '',
            euroAmount: prev.euroAmount != null ? String(Math.abs(Number(prev.euroAmount))) : '',
            notes: prev.notes ?? ''
        }
        : {
            date: entry?.date ?? today,
            financialAssetId: entry?.financialAsset.id ?? null,
            newAssetName: '',
            newAssetFundId: investmentFunds[0]?.id ?? null,
            assetAmount: entry ? String(Math.abs(Number(entry.assetAmount))) : '',
            euroAmount: entry ? String(Math.abs(Number(entry.euroAmount))) : '',
            notes: entry?.notes ?? ''
        }

    const handleSubmit = (values: typeof initialValues) => {
        const assetAmount = Number(values.assetAmount)
        const euroAmount = Number(values.euroAmount)
        completion({
            date: values.date,
            assetAmount: String(mode === 'buy' ? assetAmount : -assetAmount),
            euroAmount: String(mode === 'buy' ? -euroAmount : euroAmount),
            notes: values.notes || null,
            ...(values.financialAssetId
                ? { financialAssetId: Number(values.financialAssetId) }
                : {
                    createAsset: {
                        name: values.newAssetName.trim(),
                        fundId: Number(values.newAssetFundId)
                    }
                })
        })
    }

    return (
        <Formik validationSchema={schema} onSubmit={handleSubmit} initialValues={initialValues}>
            {({ handleSubmit, handleChange, values, errors, setFieldValue }) => {
                const assetOptions: AssetOption[] = assets.map(a => ({
                    value: a.id,
                    label: `${a.name} (${a.fund.name})`
                }))

                const selectedAssetOption = values.financialAssetId !== null
                    ? assetOptions.find(option => option.value === values.financialAssetId) ?? null
                    : values.newAssetName
                        ? { value: `new:${values.newAssetName}`, label: values.newAssetName, __isNew__: true }
                        : null

                const handleAssetChange = (selected: SingleValue<AssetOption>, _meta: ActionMeta<AssetOption>) => {
                    if (!selected) {
                        setFieldValue('financialAssetId', null)
                        setFieldValue('newAssetName', '')
                        return
                    }

                    if (selected.__isNew__) {
                        setFieldValue('financialAssetId', null)
                        setFieldValue('newAssetName', selected.label)
                        if (!values.newAssetFundId && investmentFunds.length > 0) {
                            setFieldValue('newAssetFundId', investmentFunds[0].id)
                        }
                        return
                    }

                    setFieldValue('financialAssetId', Number(selected.value))
                    setFieldValue('newAssetName', '')
                }

                const handleCreateAssetOption = (inputValue: string) => {
                    setFieldValue('financialAssetId', null)
                    setFieldValue('newAssetName', inputValue)
                    if (!values.newAssetFundId && investmentFunds.length > 0) {
                        setFieldValue('newAssetFundId', investmentFunds[0].id)
                    }
                }

                return (
                <Form noValidate onSubmit={handleSubmit}>
                    {hasLaterSells && (
                        <Alert variant="warning">
                            ⚠ This asset has {laterSellsCount} later sale
                            transaction{laterSellsCount === 1 ? '' : 's'} (same or later date). Saving this
                            transaction will recompute the realized gain of all those transactions
                            automatically. If recomputing fails (e.g. the change makes a sale illegal),
                            the whole operation will be rolled back.
                        </Alert>
                    )}
                    <Form.Row>
                        <Form.Group as={Col} controlId="txDate">
                            <Form.Label>Date</Form.Label>
                            <Form.Control
                                type="text"
                                name="date"
                                placeholder="YYYY-MM-DD"
                                value={values.date}
                                onChange={handleChange}
                                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const raw = e.target.value.trim()
                                    const parsed = moment(raw, ['YYYY-MM-DD', 'YYYY/MM/DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'M/D/YYYY', 'D/M/YYYY'], true)
                                    if (parsed.isValid()) {
                                        setFieldValue('date', parsed.format('YYYY-MM-DD'))
                                    }
                                }}
                                isInvalid={!!errors.date}
                            />
                            <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} controlId="txAsset">
                            <Form.Label>Asset</Form.Label>
                            <CreatableSelect
                                classNamePrefix="asset-select"
                                isClearable
                                options={assetOptions}
                                value={selectedAssetOption}
                                onChange={handleAssetChange}
                                onCreateOption={handleCreateAssetOption}
                                placeholder="Search for an asset..."
                                formatCreateLabel={(input: string) => `Add new asset "${input}"...`}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '38px',
                                    }),
                                }}
                            />
                            {errors.financialAssetId && (
                                <div className="invalid-feedback d-block">
                                    {errors.financialAssetId}
                                </div>
                            )}
                        </Form.Group>
                    </Form.Row>
                    {values.newAssetName && (
                        <Form.Row>
                            <Form.Group as={Col} controlId="txNewAssetFund">
                                <Form.Label>Fund for new asset</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="newAssetFundId"
                                    value={values.newAssetFundId ?? ''}
                                    onChange={handleChange}
                                    isInvalid={!!errors.newAssetFundId}
                                >
                                    {investmentFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">{errors.newAssetFundId}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>
                    )}
                    <Form.Row>
                        <Form.Group as={Col} controlId="txAssetAmount">
                            <Form.Label>{mode === 'buy' ? 'Amount bought' : 'Amount sold'}</Form.Label>
                            <Form.Control
                                type="number"
                                name="assetAmount"
                                placeholder="0.00000000"
                                step="0.00000001"
                                value={values.assetAmount}
                                onChange={handleChange}
                                isInvalid={!!errors.assetAmount}
                            />
                            <Form.Control.Feedback type="invalid">{errors.assetAmount}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} controlId="txEuroAmount">
                            <Form.Label>{mode === 'buy' ? '€ Cost' : '€ Proceeds'}</Form.Label>
                            <Form.Control
                                type="number"
                                name="euroAmount"
                                placeholder="0.00"
                                step="0.01"
                                value={values.euroAmount}
                                onChange={handleChange}
                                isInvalid={!!errors.euroAmount}
                            />
                            <Form.Control.Feedback type="invalid">{errors.euroAmount}</Form.Control.Feedback>
                            {mode === 'buy' && <Form.Text className="text-muted">Stored as a negative euro amount in the API.</Form.Text>}
                        </Form.Group>
                    </Form.Row>
                    <Form.Group controlId="txNotes">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            type="text"
                            name="notes"
                            placeholder="Optional notes"
                            value={values.notes}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Button variant="primary" type="submit">{mode === 'sell' ? 'Preview' : 'Save'}</Button>
                </Form>
                )
            }}
        </Formik>
    )
}

function SellPreviewConfirmation({ preview }: { preview: SellPreview }) {
    const [showDetails, setShowDetails] = React.useState(false)
    const profitOrLoss = Number(preview.profitOrLoss)
    const isGain = profitOrLoss >= 0
    return (
        <div>
            <Table bordered size="sm" responsive>
                <tbody>
                    <tr>
                        <td>Total amount sold</td>
                        <td className="text-right"><strong>{Utils.formatPreciseAmount(preview.sellAmount) as string}</strong></td>
                    </tr>
                    <tr>
                        <td>Proceeds</td>
                        <td className="text-right"><strong>{Utils.formatMoney(preview.proceeds) as string} €</strong></td>
                    </tr>
                    <tr>
                        <td>Cost basis</td>
                        <td className="text-right"><strong>{Utils.formatMoney(preview.costBasis) as string} €</strong></td>
                    </tr>
                    <tr>
                        <td>{isGain ? 'Gain' : 'Loss'}</td>
                        <td className="text-right">
                            <strong className={isGain ? 'text-success' : 'text-danger'}>
                                {Utils.formatMoney(preview.profitOrLoss) as string} €
                            </strong>
                        </td>
                    </tr>
                    <tr>
                        <td>Remaining quantity after sale</td>
                        <td className="text-right"><strong>{Utils.formatPreciseAmount(preview.quantityRemainingAfter) as string}</strong></td>
                    </tr>
                </tbody>
            </Table>
            <Button variant="link" className="p-0" onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? 'Hide details' : 'details...'}
            </Button>
            {showDetails && (
                <>
                    <p className="mt-2">This sale would consume the following batches (FIFO):</p>
                    <Table bordered size="sm" responsive>
                        <thead>
                            <tr>
                                <th>Buy date</th>
                                <th className="text-right">Amount consumed</th>
                                <th className="text-right">Avg cost / unit</th>
                                <th className="text-right">Cost basis</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preview.consumedBatches.length === 0 ? (
                                <tr><td colSpan={4} className="text-center text-muted">No batches would be consumed.</td></tr>
                            ) : preview.consumedBatches.map((b, i) => (
                                <tr key={i}>
                                    <td>{b.date}</td>
                                    <td className="text-right">{Utils.formatPreciseAmount(b.amount) as string}</td>
                                    <td className="text-right">{Utils.formatPreciseAmount(b.averageCost) as string} €</td>
                                    <td className="text-right">{Utils.formatMoney(b.originalCost) as string} €</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    )
}

export default EditAssetTransactionWorkflow